<script setup lang="ts">
import RightArrowSvg from '~/assets/svg/right-arrow.svg?component';
import { useStoryblokVersion } from '~/composables/use-storyblok-version';

const i18n = useI18n();
const route = useRoute();

const props = defineProps({
    blok: {
        type: Object,
        default: () => ({}),
    },
});

const currentMarket = route.query.market as Market | null;

const storyblokApi = useStoryblokApi();
const { data } = await storyblokApi.get('cdn/stories', {
    version: useStoryblokVersion(),
    resolve_links: 'url',
    starts_with: 'blog/',
    is_startpage: false,
    language: i18n.locale.value || 'en',
});

const articles = computed(() => {
    return data.stories.filter((article: any) => {
        if (article.content.visibleIn == null || article.content.visibleIn.length === 0)
            return true;
        return article.content.visibleIn.includes(currentMarket);
    });
});

const firstArticle = computed(() => {
    return articles.value[0];
});

const restArticles = computed(() => {
    return articles.value.slice(1);
});

function blogUrl(article: any) {
    return { path: `/${article.full_slug}`, query: route.query };
}
</script>

<template>
    <section class="bg-white dark:bg-midnight-blue">
        <div class="py-8 px-4 mx-auto max-w-screen-xl sm:py-16 lg:px-6">
            <div class="mx-auto max-w-screen-sm text-center">
                <h1
                    class="mb-4 text-3xl lg:text-4xl tracking-tight font-extrabold text-gray-900 dark:text-white"
                >
                    {{ blok.title }}
                </h1>
                <p class="mb-8 lg:mb-16 font-light text-gray-500 dark:text-gray-400 sm:text-xl">
                    {{ blok.description }}
                </p>
            </div>
            <div
                class="grid gap-8 mb-16 lg:divide-x lg:divide-gray-200 dark:lg:divide-gray-700 lg:grid-cols-3"
            >
                <article>
                    <NuxtLink :to="blogUrl(firstArticle)">
                        <img
                            class="mb-5 rounded-lg"
                            :src="firstArticle.content.image.filename"
                            alt="blog image"
                        />
                    </NuxtLink>
                    <h2
                        class="my-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white"
                    >
                        <NuxtLink :to="blogUrl(firstArticle)">{{
                            firstArticle.content.title
                        }}</NuxtLink>
                    </h2>
                    <p class="mb-4 font-light text-gray-500 dark:text-gray-400">
                        {{ firstArticle.content.teaser }}
                    </p>
                    <NuxtLink
                        :to="blogUrl(firstArticle)"
                        class="inline-flex items-center text-lg font-medium text-primary-600 hover:text-primary-800 dark:text-primary-500 dark:hover:text-primary-700"
                    >
                        {{ $t('blog.readMore') }}
                        <RightArrowSvg class="ml-2 w-4 h-4" />
                    </NuxtLink>
                </article>
                <div class="space-y-8 lg:pl-8">
                    <article
                        v-for="article in restArticles.slice(0, Math.ceil(restArticles.length / 2))"
                    >
                        <h2
                            class="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white"
                        >
                            <NuxtLink :to="blogUrl(article)">{{ article.content.title }}</NuxtLink>
                        </h2>
                        <p class="mb-4 font-light text-gray-500 dark:text-gray-400">
                            {{ article.content.teaser }}
                        </p>
                        <NuxtLink
                            :to="blogUrl(article)"
                            class="inline-flex items-center text-lg font-medium text-primary-600 hover:text-primary-800 dark:text-primary-500 dark:hover:text-primary-700"
                        >
                            {{ $t('blog.readMore') }}
                            <RightArrowSvg class="ml-2 w-4 h-4" />
                        </NuxtLink>
                    </article>
                </div>
                <div class="space-y-8 lg:pl-8">
                    <article
                        v-for="article in restArticles.slice(Math.ceil(restArticles.length / 2))"
                    >
                        <h2
                            class="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white"
                        >
                            <NuxtLink :to="blogUrl(article)">{{ article.content.title }}</NuxtLink>
                        </h2>
                        <p class="mb-4 font-light text-gray-500 dark:text-gray-400">
                            {{ article.content.teaser }}
                        </p>
                        <NuxtLink
                            :to="blogUrl(article)"
                            class="inline-flex items-center text-lg font-medium text-primary-600 hover:text-primary-800 dark:text-primary-500 dark:hover:text-primary-700"
                        >
                            {{ $t('blog.readMore') }}
                            <RightArrowSvg class="ml-2 w-4 h-4" />
                        </NuxtLink>
                    </article>
                </div>
            </div>
        </div>
    </section>
</template>
